'use client';

import * as React from 'react';
import type { LineDrawShapeProps, PieSectorShapeProps } from 'recharts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  getNiceTickValues,
  Line,
  LineChart,
  LineDrawShape,
  matchByDataKey,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  useChartWidth,
  XAxis,
  YAxis,
} from 'recharts';
import {
  estimateChartAxisWidth,
  getFiniteChartValue,
  getFinitePositiveChartValue,
  getNonNegativeChartValue,
} from '@/components/molecules/charts/chart-data';
import { useAIInteraction } from '@/components/organisms/ai/_shared/interaction-context';
import { validateChartBlockSchema } from '@/components/organisms/ai/_shared/schema-validation';
import { CHART_PALETTE, useChartKeyColors } from '@/components/ui/chart';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

/**
 * Schema for configuring an AIChart
 */
export interface ChartBlockSchema {
  /** Unique identifier for the component instance */
  id: string;
  /** Chart type */
  type: 'bar' | 'line' | 'pie' | 'area';
  /** Optional title displayed above the chart */
  title?: string;
  /** Array of data points */
  data: Record<string, unknown>[];
  /** Key for X-axis values */
  xKey?: string;
  /** Key(s) for Y-axis values - can be a single key or array for multiple series */
  yKey?: string | string[];
  /** Custom colors for the chart series */
  colors?: string[];
  /** Whether the chart responds to click events */
  interactive?: boolean;
  /** Height of the chart in pixels (default: 300) */
  height?: number;
}

/**
 * Props for AIChart
 */
export interface AIChartProps {
  /** Schema defining the chart configuration */
  schema: ChartBlockSchema;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_HEIGHT = 300;
const AXIS_TICK_STYLE = {
  fill: 'var(--muted-foreground)',
  fontSize: 12,
};
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
};
const PLOT_SELECTOR =
  '.recharts-bar-rectangle path, .recharts-line-curve, .recharts-area-curve, .recharts-pie-sector path';
const FALLBACK_UPDATE_MS = 200;
const UPDATE_EASING = 'ease-out' as const;
const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Normalize yKey to always be an array
 */
function normalizeYKeys(yKey: string | string[] | undefined): string[] {
  if (!yKey) return ['value'];
  return Array.isArray(yKey) ? yKey : [yKey];
}

function isChartDatum(value: unknown): value is Record<string, unknown> {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return true;
  }

  return false;
}

function toChartDatum(value: unknown): Record<string, unknown> {
  if (isChartDatum(value)) {
    return value;
  }

  return {};
}

function getPayloadDatum(value: unknown): Record<string, unknown> | undefined {
  const datum = toChartDatum(value);
  const payload = datum.payload;

  if (isChartDatum(payload)) {
    return payload;
  }

  return undefined;
}

function getStringField(
  datum: Record<string, unknown>,
  field: string,
): string | undefined {
  const value = datum[field];

  return typeof value === 'string' ? value : undefined;
}

function sanitizeChartFieldValue(
  value: unknown,
  chartType: ChartBlockSchema['type'],
) {
  if (typeof value !== 'number') {
    return value;
  }

  return chartType === 'pie'
    ? getNonNegativeChartValue(value)
    : getFiniteChartValue(value);
}

function getChartDatumLabel(
  datum: Record<string, unknown>,
  xKey: string,
  index: number,
): string {
  const value = datum[xKey];
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return `Data point ${index + 1}`;
}

/** Update duration from `--motion-duration-moderate` (180-220 ms band). */
function readUpdateDurationMs(doc: Document): number {
  const view = doc.defaultView;
  if (!view) return FALLBACK_UPDATE_MS;
  const raw = view
    .getComputedStyle(doc.documentElement)
    .getPropertyValue('--motion-duration-moderate')
    .trim();
  const ms = raw.endsWith('ms')
    ? Number.parseFloat(raw)
    : Number.parseFloat(raw) * 1000;
  return ms >= 180 && ms <= 220 ? ms : FALLBACK_UPDATE_MS;
}

/**
 * Renderer animation policy (reuses the canonical charts' reduced-motion
 * hook). Recharts only interpolates from geometry it already drew, so:
 * - nothing animates until the first plot exists and has been re-rendered once
 *   (Recharts records drawn geometry on re-render), so mount never draws on and
 *   empty/invalid -> valid (a fresh mount) is immediate;
 * - a series key seen for the first time is placed immediately, then joins;
 * - a changed Y scale (domain/ticks or axis gutter) replaces immediately:
 *   geometry never interpolates in the old coordinates under the new ticks;
 * - reduced motion disables interpolation entirely.
 * Chart-type switches remount (the container is keyed on `type`), so never morph.
 */
function useChartMotion(
  yKeys: string[],
  hostRef: React.RefObject<HTMLElement | null>,
  scaleKey: string,
) {
  const reduced = useReducedMotion();
  const committedScale = React.useRef(scaleKey);
  const scaleStable = committedScale.current === scaleKey;
  React.useEffect(() => {
    committedScale.current = scaleKey;
  }, [scaleKey]);
  const [durationMs, setDurationMs] = React.useState(FALLBACK_UPDATE_MS);
  React.useEffect(() => {
    const doc = hostRef.current?.ownerDocument;
    if (doc) setDurationMs(readUpdateDurationMs(doc));
  }, [hostRef]);
  const [armed, setArmed] = React.useState(false);
  const [known, setKnown] = React.useState(() => new Set(yKeys));
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    let frame = 0;
    let tries = 0;
    const poll = () => {
      if (hostRef.current?.querySelector(PLOT_SELECTOR)) {
        rerender();
        frame = requestAnimationFrame(() => setArmed(true));
      } else if (tries++ < 180) {
        frame = requestAnimationFrame(poll);
      }
    };
    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
  }, [hostRef]);

  React.useEffect(() => {
    if (yKeys.every((key) => known.has(key))) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      rerender();
      second = requestAnimationFrame(() => setKnown(new Set(yKeys)));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [yKeys, known]);

  return (key: string) => ({
    isAnimationActive: !reduced && armed && scaleStable && known.has(key),
    animationBegin: 0,
    animationDuration: durationMs,
    animationEasing: UPDATE_EASING,
  });
}

interface AIChartBodyProps {
  type: ChartBlockSchema['type'];
  hostRef: React.RefObject<HTMLElement | null>;
  chartData: Record<string, unknown>[];
  xKey: string;
  yKeys: string[];
  seriesColors: string[];
  colors?: string[];
  interactive: boolean;
  activeDot: Record<string, unknown>;
  onBarClick: (barData: unknown) => void;
  onDatumClick: (datum: Record<string, unknown>, label?: string) => void;
}

/** Line without the stroke-dash reveal, so a line is never drawn on. */
function LineStaticShape(props: LineDrawShapeProps): React.ReactElement {
  return <LineDrawShape {...props} visibleLength={null} />;
}

type PieTargets = readonly number[];

/**
 * Recharts unmounts Pie `label`s while sectors animate, and would move them
 * with the animated angle. Labels are drawn in the sector shape at the FINAL
 * mid-angle (from the new data), so they stay visible and stationary. Same
 * anchor and class as the built-in label, with semantic text colour; long names are truncated to
 * stay inside the svg.
 */
function PieSectorWithLabel({
  targets,
  ...props
}: Partial<PieSectorShapeProps> & {
  targets: PieTargets;
}): React.ReactElement {
  const { cx = 0, cy = 0, outerRadius = 0 } = props;
  const chartWidth = useChartWidth() ?? 0;
  const full =
    typeof props.name === 'string' || typeof props.name === 'number'
      ? String(props.name)
      : '';
  const midAngle = props.index === undefined ? undefined : targets[props.index];
  const rad = midAngle === undefined ? 0 : (-Math.PI / 180) * midAngle;
  const x = cx + (outerRadius + 20) * Math.cos(rad);
  const y = cy + (outerRadius + 20) * Math.sin(rad);
  const atStart = x > cx;
  const room = Math.max(0, (atStart ? chartWidth - x : x) - 2);
  const ref = React.useRef<SVGTextElement>(null);
  const [fit, setFit] = React.useState({ of: full, room: -1, text: full });
  const shown = fit.of === full ? fit.text : full;
  const measured = React.useRef({ of: '', perChar: 0 });
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !chartWidth) return;
    // Measure only the untruncated text so the result cannot oscillate.
    if (shown === full) {
      measured.current = {
        of: full,
        perChar: el.getComputedTextLength() / Math.max(1, full.length),
      };
    }
    if (measured.current.of !== full || measured.current.perChar <= 0) return;
    let text = shown;
    if (fit.of !== full || fit.room !== room) {
      // Width changed (either direction): recompute from the full text.
      const chars = Math.floor(room / measured.current.perChar);
      text =
        full.length <= chars
          ? full
          : chars < 2
            ? ''
            : `${full.slice(0, chars - 1)}…`;
    } else if (
      text !== '' &&
      text !== full &&
      el.getComputedTextLength() > room
    ) {
      // "…" is not average width: shrink until the real text fits.
      text = text.length > 2 ? `${text.slice(0, -2)}…` : '';
    }
    if (text !== fit.text || fit.room !== room || fit.of !== full) {
      setFit({ of: full, room, text });
    }
  }, [full, shown, room, chartWidth, fit]);
  return (
    <g>
      <Sector {...props} />
      {midAngle !== undefined && shown !== full && (
        // Native tooltip only (not AT-reliable: the chart root is role="img").
        <title>{full}</title>
      )}
      {midAngle !== undefined && (
        <text
          ref={ref}
          x={x}
          y={y}
          fill="var(--text-secondary)"
          stroke="none"
          className="recharts-text recharts-pie-label-text"
          textAnchor={atStart ? 'start' : 'end'}
          dominantBaseline="middle"
          data-slot="ai-chart-pie-label"
        >
          {shown}
        </text>
      )}
    </g>
  );
}

/** Final mid-angle (degrees) of each slice, by position in the data. */
function getPieTargets(
  data: Record<string, unknown>[],
  yKey: string | undefined,
): PieTargets {
  const values = data.map((d) => {
    const v = yKey === undefined ? 0 : d[yKey];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
  });
  const sum = values.reduce((a, b) => a + b, 0);
  let acc = 0;
  return values.map((v) => {
    const span = sum > 0 ? (v / sum) * 360 : 0;
    acc += span;
    return acc - span / 2;
  });
}

/** Y scale identity: nice ticks + axis gutter (both move the plot geometry). */
function getScaleKey(
  type: ChartBlockSchema['type'],
  data: Record<string, unknown>[],
  yKeys: string[],
  gutter: number,
): string {
  if (type === 'pie') return 'pie';
  const nums = data
    .flatMap((d) => yKeys.map((k) => d[k]))
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const lo = Math.min(0, ...nums);
  const hi = Math.max(0, ...nums);
  return `${getNiceTickValues([lo, hi], 5, true).join(',')}|${gutter}`;
}

function AIChartBody({
  type,
  hostRef,
  chartData,
  xKey,
  yKeys,
  seriesColors,
  colors,
  interactive,
  activeDot,
  onBarClick,
  onDatumClick,
}: AIChartBodyProps): React.ReactElement | null {
  const matchBy = React.useMemo(() => matchByDataKey(xKey), [xKey]);
  const yAxisWidth = React.useMemo(
    () =>
      estimateChartAxisWidth({
        values: chartData.flatMap((datum) => yKeys.map((key) => datum[key])),
        minWidth: 48,
        maxWidth: 112,
      }),
    [chartData, yKeys],
  );
  const motion = useChartMotion(
    yKeys,
    hostRef,
    getScaleKey(type, chartData, yKeys, yAxisWidth),
  );
  const pieTargets = React.useMemo(
    () => getPieTargets(chartData, yKeys[0]),
    [chartData, yKeys],
  );
  const sliceId = (entry: Record<string, unknown>, index: number) =>
    String(entry[xKey] ?? index);
  const sliceColors = useChartKeyColors(
    chartData.map(sliceId),
    colors?.length ? colors : CHART_PALETTE,
  );
  const sliceColor = (entry: Record<string, unknown>, index: number) =>
    sliceColors.get(sliceId(entry, index));

  switch (type) {
    case 'bar':
      return (
        <BarChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            className="stroke-border"
          />
          <XAxis
            dataKey={xKey}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
            tickMargin={8}
            width={yAxisWidth}
          />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
          {yKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={seriesColors[index]}
              radius={BAR_RADIUS}
              {...motion(key)}
              animationMatchBy={matchBy}
              cursor={interactive ? 'pointer' : undefined}
              onClick={onBarClick}
            >
              {chartData.map((entry, cellIndex) => (
                <Cell
                  data-slot="ai-chart-bar"
                  fill={seriesColors[index]}
                  key={`bar-cell-${key}-${entry[xKey] ?? cellIndex}`}
                />
              ))}
            </Bar>
          ))}
        </BarChart>
      );

    case 'line':
      return (
        <LineChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            className="stroke-border"
          />
          <XAxis
            dataKey={xKey}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
            tickMargin={8}
            width={yAxisWidth}
          />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
          {yKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={seriesColors[index]}
              strokeWidth={2}
              dot={{ fill: seriesColors[index], r: 4 }}
              shape={LineStaticShape}
              {...motion(key)}
              animationMatchBy={matchBy}
              activeDot={activeDot}
            />
          ))}
        </LineChart>
      );

    case 'area':
      return (
        <AreaChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            className="stroke-border"
          />
          <XAxis
            dataKey={xKey}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK_STYLE}
            tickMargin={8}
            width={yAxisWidth}
          />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
          {yKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={seriesColors[index]}
              fill={seriesColors[index]}
              fillOpacity={0.3}
              {...motion(key)}
              animationMatchBy={matchBy}
              strokeWidth={2}
              activeDot={activeDot}
            />
          ))}
        </AreaChart>
      );

    case 'pie':
      return (
        <PieChart>
          <Pie
            data={chartData}
            dataKey={yKeys[0]}
            nameKey={xKey}
            cx="50%"
            cy="50%"
            outerRadius="70%"
            shape={<PieSectorWithLabel targets={pieTargets} />}
            {...motion(yKeys[0])}
            animationMatchBy={matchBy}
            cursor={interactive ? 'pointer' : undefined}
            onClick={(pieData) => {
              const datum = toChartDatum(pieData);
              onDatumClick(datum, getStringField(datum, 'name'));
            }}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}-${entry[xKey] ?? ''}`}
                fill={sliceColor(entry, index)}
              />
            ))}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
        </PieChart>
      );

    default:
      return null;
  }
}

// =============================================================================
// Component
// =============================================================================

function InvalidChartSchema({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div
      data-slot="ai-chart"
      role="alert"
      className={cn(
        'rounded-lg border border-destructive bg-destructive/20 p-4 text-sm text-destructive',
        className,
      )}
    >
      Invalid chart schema
    </div>
  );
}

/**
 * AIChart - Renders charts from JSON schema in AI chat messages.
 *
 * Supports bar, line, pie, and area chart types with optional interactivity.
 * When interactive mode is enabled, click events are emitted through the
 * AIInteractionContext.
 */
export function AIChart({
  schema,
  className,
}: AIChartProps): React.ReactElement {
  const { onInteraction } = useAIInteraction();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const chartSchema = React.useMemo(
    () => (validateChartBlockSchema(schema) ? schema : null),
    [schema],
  );

  const id = chartSchema?.id ?? '';
  const type = chartSchema?.type ?? 'bar';
  const title = chartSchema?.title;
  const data = chartSchema?.data ?? [];
  const xKey = chartSchema?.xKey ?? 'name';
  const yKey = chartSchema?.yKey;
  const colors = chartSchema?.colors;
  const interactive = chartSchema?.interactive ?? false;
  const height = chartSchema?.height ?? DEFAULT_HEIGHT;

  const yKeys = React.useMemo(() => normalizeYKeys(yKey), [yKey]);
  const chartData = React.useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((datum) => {
        const chartDatum = toChartDatum(datum);
        const sanitizedDatum: Record<string, unknown> = { ...chartDatum };
        for (const key of yKeys) {
          sanitizedDatum[key] = sanitizeChartFieldValue(chartDatum[key], type);
        }
        return sanitizedDatum;
      }),
    [data, type, yKeys],
  );
  const keyColors = useChartKeyColors(yKeys);
  const seriesColors = React.useMemo(
    () =>
      yKeys.map((key, index) =>
        colors?.length
          ? colors[index % colors.length]
          : (keyColors.get(key) ?? CHART_PALETTE[0]),
      ),
    [colors, yKeys, keyColors],
  );
  const chartHeight = getFinitePositiveChartValue(height, DEFAULT_HEIGHT);
  const chartHeightStyle = React.useMemo(
    () => ({ height: chartHeight }),
    [chartHeight],
  );

  // Handle click events on chart elements
  const handleClick = React.useCallback(
    (clickedData: Record<string, unknown>, label?: string) => {
      if (!interactive) return;

      onInteraction?.({
        componentId: id,
        componentType: 'chart',
        action: 'click',
        value: {
          label: label ?? clickedData[xKey],
          ...clickedData,
        },
        timestamp: Date.now(),
      });
    },
    [interactive, onInteraction, id, xKey],
  );
  const handleBarClick = React.useCallback(
    (barData: unknown) => {
      handleClick(getPayloadDatum(barData) ?? toChartDatum(barData));
    },
    [handleClick],
  );
  const handleActiveDotClick = React.useCallback(
    (_event: unknown, payload: unknown) => {
      const datum = getPayloadDatum(payload);
      if (datum) {
        handleClick(datum);
      }
    },
    [handleClick],
  );
  const activeDot = React.useMemo(
    () => ({
      r: 6,
      cursor: interactive ? 'pointer' : undefined,
      onClick: handleActiveDotClick,
    }),
    [handleActiveDotClick, interactive],
  );
  const keyboardItems = React.useMemo(
    () =>
      chartData.map((datum, index) => ({
        id: `${id || 'chart'}-${index}`,
        label: getChartDatumLabel(datum, xKey, index),
        datum,
      })),
    [chartData, id, xKey],
  );

  // Generate accessible summary of chart data
  const chartLabel = title
    ? `${title} - ${type} chart with ${chartData.length} data points`
    : `${type} chart with ${chartData.length} data points`;

  if (!chartSchema) {
    return <InvalidChartSchema className={className} />;
  }

  // Empty state
  if (chartData.length === 0) {
    return (
      <div
        data-slot="ai-chart"
        role="img"
        aria-label={
          title ? `${title} - No data available` : 'Chart - No data available'
        }
        className={cn(
          'flex items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground',
          className,
        )}
        style={chartHeightStyle}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      data-slot="ai-chart"
      role="img"
      aria-label={chartLabel}
      className={cn('flex flex-col gap-2', className)}
    >
      {title && (
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
      )}
      <div ref={hostRef} style={chartHeightStyle}>
        <ResponsiveContainer
          key={type}
          width="100%"
          height="100%"
          minWidth={0}
          initialDimension={{ width: 1, height: 1 }}
        >
          <AIChartBody
            type={type}
            hostRef={hostRef}
            chartData={chartData}
            xKey={xKey}
            yKeys={yKeys}
            seriesColors={seriesColors}
            colors={colors}
            interactive={interactive}
            activeDot={activeDot}
            onBarClick={handleBarClick}
            onDatumClick={handleClick}
          />
        </ResponsiveContainer>
      </div>
      {interactive && (
        <fieldset
          data-slot="ai-chart-keyboard-controls"
          className="m-0 flex min-w-0 max-h-24 flex-wrap gap-1.5 overflow-auto border-0 p-0"
        >
          <legend className="sr-only">Chart data point controls</legend>
          {keyboardItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleClick(item.datum, item.label)}
              className="min-h-9 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {item.label}
            </button>
          ))}
        </fieldset>
      )}
    </div>
  );
}

AIChart.displayName = 'AIChart';

export default AIChart;
